import type { Maybe } from "@/domains/common/types";
import type { Uuid } from "@/domains/global/identifiers";
import { AssistantRoutineObservable } from "@/store/assistant/AssistantRoutineObservable";
import { BaseCurrentAssistantSyncModelStore } from "@/store/assistant/base-current-assistant-sync-model-store";
import type { AssistantRoutineIndexData, AssistantRoutineModelData } from "@/store/assistant/types";
import type { AppStore } from "@/store/AppStore";
import { type OptimisticSyncUpdate, SyncModelKind, type SyncUpdateValue } from "@/store/sync/types";
import type { AppSubStoreArgs } from "@/store/types";
import { makeObservable, override } from "mobx";

export class AppStoreAssistantRoutinesStore extends BaseCurrentAssistantSyncModelStore<
  AssistantRoutineObservable,
  AssistantRoutineModelData
> {
  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.AssistantRoutine, ...injectedDeps });

    makeObservable<this, "createSyncModel">(this, {
      createSyncModel: false,
      computeIndexes: override,
      getAssistantRoutineObservableById: false,
    });
  }

  createSyncModel(data: SyncUpdateValue<AssistantRoutineModelData>): AssistantRoutineObservable {
    return new AssistantRoutineObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  public computeIndexes({
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<AssistantRoutineModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<AssistantRoutineModelData>[];
  }): AssistantRoutineIndexData {
    const syncUpdateValue =
      (optimisticUpdates.at(-1)?.value as SyncUpdateValue<AssistantRoutineModelData> | undefined) ||
      remoteData;
    const modelData = syncUpdateValue?.model_data;

    return {
      assistant_id: modelData?.assistant_id ?? "",
      kind: modelData?.kind ?? "",
      status: modelData?.status ?? "",
      next_run_at: modelData?.next_run_at ?? "",
      last_run_at: modelData?.last_run_at ?? "",
      created_at: modelData?.created_at ?? "",
      updated_at: modelData?.updated_at ?? "",
    };
  }

  getAssistantRoutineObservableById({
    assistantRoutineId,
  }: {
    assistantRoutineId: Uuid;
  }): AssistantRoutineObservable | undefined {
    return this.get(assistantRoutineId);
  }
}
